/**
 * The signed-in session, as React context.
 *
 * One bootstrap call fills it: the principal, the navigation built from the
 * role's grants, the permission map, the locale, the palette, the currencies
 * and the interface strings. Everything else in the application reads from
 * here rather than fetching again.
 *
 * `can` deserves a note. It answers whether to draw a button, and that is all
 * it is for. The server checks every action again when it is taken, so a
 * tampered permission map buys a user a button that refuses. Treating this as
 * the authority would be the classic mistake of a single page application over
 * a permissioned platform: the gate has to be where the data is.
 */
import { createContext, useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api/client'
import type { Bootstrap, Currency, Locale } from '../api/types'
import { DASH, date, datetime, label, money, nameOf, num } from '../format/format'

interface SessionValue {
  boot: Bootstrap
  locale: Locale
  dir: 'ltr' | 'rtl'
  /** An interface string, by key. Falls back to the key's own default rather
   *  than rendering the key, which is a defect a user would see. */
  t: (key: string, fallback?: string) => string
  can: (sectionKey: string, action?: string) => boolean
  grant: (sectionKey: string) => Bootstrap['permissions'][string] | undefined
  currency: (code?: string | null) => Currency | undefined
  amount: (value: unknown, code?: string | null, places?: number) => string
  n: (value: unknown, places?: number) => string
  d: (value: unknown) => string
  dt: (value: unknown) => string
  lbl: (value: unknown) => string
  nm: (row: Record<string, unknown> | null | undefined, base?: string) => string
  refresh: () => void
}

const SessionContext = createContext<SessionValue | null>(null)

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession outside a SessionProvider')
  return value
}

export function useBootstrapQuery() {
  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: api.bootstrap,
    retry: false,
    staleTime: 60_000,
  })
}

export function SessionProvider({ boot, children }:
                                { boot: Bootstrap; children: ReactNode }) {
  const client = useQueryClient()
  const currencyIndex = useMemo(() => {
    const index = new Map<string, Currency>()
    for (const c of boot.currencies) index.set(c.currency_code, c)
    return index
  }, [boot.currencies])

  const currency = useCallback((code?: string | null) => {
    if (code && currencyIndex.has(code)) return currencyIndex.get(code)
    return currencyIndex.get(boot.presentation_currency)
  }, [currencyIndex, boot.presentation_currency])

  const value = useMemo<SessionValue>(() => ({
    boot,
    locale: boot.locale,
    dir: boot.locale.direction,
    t: (key, fallback) => boot.strings[key] ?? fallback ?? key,
    can: (sectionKey, action = 'view') =>
      Boolean(boot.permissions[sectionKey]?.actions.includes(action)),
    grant: (sectionKey) => boot.permissions[sectionKey],
    currency,
    amount: (v, code, places) =>
      v === null || v === undefined ? DASH
        : money(v, currency(code), boot.locale, places),
    n: (v, places = 0) => num(v, boot.locale, places),
    d: (v) => date(v, boot.locale),
    dt: (v) => datetime(v, boot.locale),
    lbl: label,
    nm: (row, base = 'name') => nameOf(row, boot.locale, base),
    refresh: () => { void client.invalidateQueries() },
  }), [boot, currency, client])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/**
 * Put the palette on the document as custom properties, and the direction and
 * language on the html element.
 *
 * On the html element rather than on a wrapper div because direction has to
 * apply to the scrollbar, the text selection and anything rendered in a
 * portal. A wrapper would leave a drawer opening on the wrong side.
 */
export function applyTheme(boot: Bootstrap) {
  const root = document.documentElement
  root.lang = boot.locale.locale_code.split('-')[0]
  root.dir = boot.locale.direction
  const p = boot.palette
  root.style.setProperty('--accent', p.accent)
  root.style.setProperty('--accent-dark', p.accent_dark)
  root.style.setProperty('--on-accent', p.on_accent)
  root.style.setProperty('--brand-ink', p.header_ink)
  root.style.setProperty('--chip-bg', p.chip_bg)
  root.style.setProperty('--chip-ink', p.chip_ink)
}
